import Layout from '../components/Layout';
import Container from '../components/Container';
import { StaticImage } from 'gatsby-plugin-image';
import { css } from '@emotion/react';
import { spacing, Breakpoint } from '../config/styles';
import { useEffect, useState } from 'react';

const pink = '#FF2D78';
const dark = '#0D0B2A';
const navy = '#1A1756';

const styles = {
  hero: css`
    position: relative;
    background: ${dark};
    height: clamp(320px, 56.25vw, 700px);
    overflow: hidden;

    ${Breakpoint.mobile} {
      height: clamp(220px, 56.25vw, 420px);
    }
  `,

  slideContainer: css`
    position: absolute;
    inset: 0;
  `,

  slide: (active: boolean) => css`
    position: absolute;
    inset: 0;
    opacity: ${active ? 1 : 0};
    transition: opacity 1.4s ease-in-out;
  `,

  overlay: css`
    position: absolute;
    inset: 0;
    z-index: 2;
    background:
      linear-gradient(to bottom, rgba(13, 11, 42, 0.2) 0%, rgba(13, 11, 42, 0.92) 100%),
      linear-gradient(to right, rgba(13, 11, 42, 0.6) 0%, transparent 70%);
    pointer-events: none;
  `,

  heroContent: css`
    position: absolute;
    bottom: ${spacing['50']};
    left: 0;
    right: 0;
    z-index: 3;
    padding: 0 ${spacing['40']};

    ${Breakpoint.mobile} {
      padding: 0 ${spacing['20']};
      bottom: ${spacing['30']};
    }
  `,

  badge: css`
    display: inline-block;
    background: ${pink};
    color: #fff;
    font-family: 'Elido', sans-serif;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    padding: 5px 14px;
    margin-bottom: ${spacing['15']};
  `,

  heroTitle: css`
    font-family: 'Elido', sans-serif;
    font-size: clamp(2.8rem, 9vw, 7.5rem);
    font-weight: 700;
    color: #fff;
    line-height: 0.88;
    margin: 0 0 ${spacing['15']};
    letter-spacing: -0.03em;
  `,

  heroDot: css`
    color: ${pink};
  `,

  heroSub: css`
    font-family: 'Elido Book', sans-serif;
    font-size: clamp(0.65rem, 1.4vw, 0.85rem);
    color: rgba(255, 255, 255, 0.45);
    letter-spacing: 0.28em;
    text-transform: uppercase;
    margin: 0;
  `,

  dots: css`
    position: absolute;
    bottom: ${spacing['20']};
    right: ${spacing['40']};
    z-index: 4;
    display: flex;
    gap: 8px;
    align-items: center;

    ${Breakpoint.mobile} {
      right: ${spacing['20']};
    }
  `,

  dot: (active: boolean) => css`
    width: ${active ? '28px' : '8px'};
    height: 8px;
    border-radius: 4px;
    background: ${active ? pink : 'rgba(255, 255, 255, 0.28)'};
    border: none;
    cursor: pointer;
    padding: 0;
    transition: width 0.35s ease, background 0.35s ease;
  `,

  section: css`
    background: ${dark};
    padding: ${spacing['70']} 0 ${spacing['100']};

    ${Breakpoint.mobile} {
      padding: ${spacing['50']} 0 ${spacing['60']};
    }
  `,

  sectionHead: css`
    display: flex;
    align-items: center;
    gap: ${spacing['20']};
    margin-bottom: ${spacing['50']};
  `,

  sectionLabel: css`
    font-family: 'Elido', sans-serif;
    font-size: 0.65rem;
    letter-spacing: 0.32em;
    text-transform: uppercase;
    color: ${pink};
    white-space: nowrap;
    margin: 0;
  `,

  sectionRule: css`
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.08);
  `,

  countLabel: css`
    font-family: 'Elido', sans-serif;
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.22);
    letter-spacing: 0.12em;
    white-space: nowrap;
  `,

  row: css`
    display: grid;
    grid-template-columns: 150px 1fr 96px;
    align-items: center;
    gap: ${spacing['30']};
    padding: ${spacing['25']} 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    transition: border-color 0.25s ease;
    cursor: default;

    &:hover {
      border-bottom-color: rgba(255, 45, 120, 0.4);
    }

    &:first-of-type {
      border-top: 1px solid rgba(255, 255, 255, 0.07);
    }

    ${Breakpoint.mobile} {
      grid-template-columns: 80px 1fr;
      gap: ${spacing['15']};
    }
  `,

  year: css`
    font-family: 'Elido', sans-serif;
    font-size: clamp(1rem, 2.2vw, 1.7rem);
    font-weight: 700;
    color: rgba(255, 255, 255, 0.16);
    line-height: 1;
    transition: color 0.25s ease;
  `,

  text: css`
    font-family: 'FreightText Pro', serif;
    font-size: clamp(0.9rem, 1.8vw, 1.2rem);
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.4;
  `,

  place: (p: number) => css`
    font-family: 'Elido', sans-serif;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    padding: 5px 10px;
    border: 1.5px solid ${p === 1 ? '#C9A84C' : p === 2 ? '#8A9BA8' : pink};
    color: ${p === 1 ? '#C9A84C' : p === 2 ? '#8A9BA8' : pink};
    text-align: center;
    white-space: nowrap;

    ${Breakpoint.mobile} {
      display: none;
    }
  `,
};

const achievements = [
  { year: '2024–25', text: 'Aitvaro krepšinio turnyras', place: 3 },
  { year: '2023', text: "Spring'E Mažosios taurės laimėtoja", place: 1 },
  { year: '2023–24', text: 'Aitvaro krepšinio turnyras', place: 2 },
  { year: '2021–22', text: 'Aitvaro krepšinio turnyras', place: 3 },
  { year: '2019–20', text: 'Aitvaro krepšinio turnyras', place: 2 },
  { year: '2017–18', text: 'Aitvaro krepšinio turnyras', place: 1 },
  { year: '2018–19', text: 'VKL Top Sport Pavasario čempionatas · 4 lyga', place: 2 },
  { year: '2015–16', text: 'VKL draudimas.lt pavasario čempionatas · Išūkio taurė', place: 1 },
];

const placeStr = (p: number) => (p === 1 ? 'I vieta' : p === 2 ? 'II vieta' : 'III vieta');

const photos = [
  <StaticImage
    src="../images/2024-2025.jpg"
    alt="Ismegink.lt 2024–2025"
    placeholder="blurred"
    style={{ width: '100%', height: '100%' }}
    imgStyle={{ objectFit: 'contain', objectPosition: 'center center' }}
    key="a"
  />,
  <StaticImage
    src="../images/team-photo-2021-22.jpeg"
    alt="Ismegink.lt 2021–2022"
    placeholder="blurred"
    style={{ width: '100%', height: '100%' }}
    imgStyle={{ objectFit: 'contain', objectPosition: 'center center' }}
    key="b"
  />,
  <StaticImage
    src="../images/team-2019-2020.jpg"
    alt="Ismegink.lt 2019–2020"
    placeholder="blurred"
    style={{ width: '100%', height: '100%' }}
    imgStyle={{ objectFit: 'contain', objectPosition: 'center center' }}
    key="c"
  />,
];

export function Head() {
  const description = 'ismegink.lt - daugiau nei krepšinio komanda';
  return (
    <>
      <title>ismegink.lt</title>
      <meta name="description" content={description} />
      <meta property="og:title" content="ismegink.lt" />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="ismegink.lt" />
      <meta name="twitter:description" content={description} />
    </>
  );
}

export default function IndexPage() {
  const [cur, setCur] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setCur((v) => (v + 1) % photos.length), 3800);
    return () => clearInterval(t);
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section css={styles.hero}>
        <div css={styles.slideContainer}>
          {photos.map((photo, i) => (
            <div key={photo.key} css={styles.slide(cur === i)}>
              {photo}
            </div>
          ))}
        </div>

        <div css={styles.overlay} />

        <div css={styles.heroContent}>
          <div css={styles.badge}>Krepšinio komanda</div>
          <h1 css={styles.heroTitle}>
            ISMEGINK<span css={styles.heroDot}>.</span>LT
          </h1>
          <p css={styles.heroSub}>Išmėgink · Nugalėk</p>
        </div>

        <div css={styles.dots}>
          {photos.map((_, i) => (
            <button
              key={i}
              css={styles.dot(cur === i)}
              onClick={() => setCur(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Achievements */}
      <section css={styles.section}>
        <Container>
          <div css={styles.sectionHead}>
            <p css={styles.sectionLabel}>Pasiekimai</p>
            <div css={styles.sectionRule} />
            <span css={styles.countLabel}>{achievements.length} titulai</span>
          </div>

          {achievements.map((a, i) => (
            <div key={i} css={styles.row}>
              <span css={styles.year}>{a.year}</span>
              <span css={styles.text}>{a.text}</span>
              <span css={styles.place(a.place)}>{placeStr(a.place)}</span>
            </div>
          ))}
        </Container>
      </section>
    </Layout>
  );
}
